/**
 * TreasureRewardIdentityFields — the fields that say WHAT a reward is.
 *
 * Shared by the two pools an admin authors: the main treasure list
 * ({@link TreasureListFields}), whose entries are placed on the map, and the consolation pool
 * ({@link ConsolationPoolFields}), whose entries are rolled for runners-up. The two differ
 * entirely in how much you get — quantities and tiers versus chances and ranges — but a SWTOR
 * item (platform catalogue) or a custom prize is described identically in both, so that part
 * lives here.
 *
 * Deliberately covers only `cartel` and `custom`: credits and augments have no identity beyond
 * their kind, and their amount belongs to the owning editor. Rendering nothing for those is
 * the correct outcome, not a gap.
 *
 * Purely controlled, like every other field component in the map builder.
 */

import { TRL_MAP_ICONS } from '@swtorbit/shared-assets';
import { PlatformItemPicker } from './PlatformItemPicker';

/** The identity fields, as a loose bag — both pools' item unions satisfy this subset. */
export interface TreasureRewardIdentity {
  /** Platform item id (cartel rewards). */
  itemId?: string;
  /** Legacy Cartel catalogue id: present only on unconverted rewards. */
  cartelItemId?: string;
  cartelItemName?: string;
  title?: string;
  description?: string;
  icon?: string;
  iconColor?: string;
}

/**
 * An item from either pool, of which only the identity fields are read.
 *
 * The `id` is not used here; it is required so the type is not "weak" (all-optional). A weak
 * type rejects anything sharing no property with it, which is exactly the credits and augment
 * members of both unions — the very kinds this component is meant to render nothing for.
 * Anchoring on the one field every member does carry lets them through.
 */
type TreasureRewardIdentityValue = TreasureRewardIdentity & { id: string };

interface TreasureRewardIdentityFieldsProps {
  kind: 'credits' | 'augment' | 'cartel' | 'custom';
  value: TreasureRewardIdentityValue;
  onChange: (patch: TreasureRewardIdentity) => void;
  disabled?: boolean;
}

export function TreasureRewardIdentityFields({
  kind,
  value,
  onChange,
  disabled = false,
}: TreasureRewardIdentityFieldsProps) {
  if (kind === 'cartel') {
    return (
      <PlatformItemPicker
        itemId={value.itemId}
        itemName={value.cartelItemName}
        legacy={!!value.cartelItemId}
        // Picking an item links the reward: the legacy catalogue id, if any, is dropped.
        onPick={({ itemId, name }) => onChange({ itemId, cartelItemName: name, cartelItemId: undefined })}
        disabled={disabled}
      />
    );
  }

  if (kind === 'custom') {
    return (
      <div className="d-flex flex-column gap-2xs">
        <input
          type="text"
          className="input input--sm"
          value={value.title ?? ''}
          onChange={(e) => onChange({ title: e.target.value })}
          disabled={disabled}
          placeholder="Title"
          aria-label="Reward title"
        />
        <input
          type="text"
          className="input input--sm"
          value={value.description ?? ''}
          onChange={(e) => onChange({ description: e.target.value })}
          disabled={disabled}
          placeholder="Short description (optional)"
          aria-label="Reward description"
        />
        <div className="d-flex gap-2xs align-items-center">
          <select
            className="input input--sm flex-1"
            value={value.icon ?? ''}
            onChange={(e) => onChange({ icon: e.target.value || undefined })}
            disabled={disabled}
            aria-label="Reward icon"
          >
            <option value="">Default icon</option>
            {TRL_MAP_ICONS.map((icon) => (
              <option key={icon.id} value={icon.id}>{icon.label}</option>
            ))}
          </select>
          <input
            type="color"
            className="input input--sm"
            style={{ width: '3rem', padding: '2px' }}
            value={value.iconColor ?? '#ffffff'}
            onChange={(e) => onChange({ iconColor: e.target.value })}
            disabled={disabled}
            aria-label="Icon colour"
          />
        </div>
      </div>
    );
  }

  return null;
}
